import type { FC, ReactNode } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box, useTheme } from '@mui/material'
import { AgentMode, resolveAgentModeBadgeColor, resolveAgentModeBadgeLabel } from '@/index'

interface BaseBadgeProps {
  readonly mode: AgentMode
  readonly size?: 'small' | 'medium'
  readonly animated?: boolean
  readonly fullWidth?: boolean
  readonly showBorder?: boolean
  readonly icon?: ReactNode
  readonly disabled?: boolean
}

export const BaseBadge: FC<BaseBadgeProps> = ({
  mode,
  size = 'medium',
  animated = false,
  fullWidth = false,
  showBorder = true,
  icon,
  disabled = false,
}) => {
  const theme = useTheme()
  const colors = resolveAgentModeBadgeColor(mode, theme.palette.mode)
  const sizeStyles = size === 'small' ? smallSizeSx : mediumSizeSx

  return (
    <Box sx={containerSx(icon, sizeStyles, colors, showBorder, fullWidth, animated, disabled)}>
      {resolveAgentModeBadgeLabel(mode)}
      {icon}
    </Box>
  )
}

const mediumSizeSx: SxProps<Theme> = {
  fontSize: '0.81rem',
  px: 2,
  py: 0.5,
}

const smallSizeSx: SxProps<Theme> = {
  fontSize: '0.7rem',
  px: 1.5,
  py: 0.3,
}

const animatedSx: SxProps<Theme> = {
  animation: 'agentModeTransitionPulse 2s ease-in-out infinite',
  '@keyframes agentModeTransitionPulse': {
    '0%, 100%': {
      opacity: 0.5,
    },
    '50%': {
      opacity: 1,
    },
  },
}

const disabledSx: SxProps<Theme> = {
  opacity: 0.65,
  cursor: 'not-allowed',
}

const containerSx = (
  icon: ReactNode | null,
  sizeStyles: SxProps<Theme>,
  colors: { bg: string; text: string; border: string },
  showBorder: boolean,
  fullWidth: boolean,
  animated: boolean,
  disabled: boolean,
): SxProps<Theme> => {
  const styles = [
    {
      display: icon ? 'inline-flex' : 'inline-block',
      ...(icon && { alignItems: 'center', gap: 0.5 }),
      fontWeight: 600,
      borderRadius: 1,
      backgroundColor: colors.bg,
      color: colors.text,
      ...(showBorder && { border: `1px solid ${colors.border}` }),
      ...(fullWidth && { width: '100%', textAlign: 'center' }),
    },
    sizeStyles,
  ]

  if (animated) styles.push(animatedSx)
  if (disabled) styles.push(disabledSx)

  return styles as SxProps<Theme>
}
